import React from 'react';
import { 
  Trash2, 
  Download, 
  Upload, 
  Image, 
  Table2, 
  Copy, 
  Loader2,
  Save,
  FolderOpen,
  Share2,
  HelpCircle,
  Swords
} from 'lucide-react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * DeckControls - Toolbar with deck management actions
 * shadcn/ui version
 */
export default function DeckControlsShadcn({
  onClear,
  onExport,
  onImport,
  onExportImage,
  onExportTTS,
  onSendToRising,
  sendToRisingLoading = false,
  onCopyCode,
  onSavedDecks,
  onShare,
  onHelp,
  exportImageLoading = false,
  showCopyButton = false,
  isMobile = false,
  hasSavedDecks = false,
}) {
  const { t } = useLanguage();

  return (
    <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-2 whitespace-nowrap min-w-max">
      {/* Clear Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">{t('controls.clearAll', 'Clear All')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('controls.clearAll', 'Clear All')}</TooltipContent>
      </Tooltip>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Export Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onExport}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{t('controls.exportDeckCode', 'Export')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('controls.exportDeckCode', 'Export Deck Code')}</TooltipContent>
      </Tooltip>

      {/* Play on UCG Rising — opens Rising with this deck already imported.
          Sits next to Export because it is one: the same deck code, delivered
          by link instead of by clipboard. */}
      {onSendToRising && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSendToRising}
              disabled={sendToRisingLoading}
              className="gap-2"
            >
              {sendToRisingLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Swords className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">{t('common.sendToRising')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('common.sendToRisingHint')}</TooltipContent>
        </Tooltip>
      )}

      {/* Import Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onImport}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            <span className="hidden sm:inline">{t('controls.importDeckCode', 'Import')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('controls.importDeckCode', 'Import Deck Code')}</TooltipContent>
      </Tooltip>

      {/* Saved Decks Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onSavedDecks}
            className="gap-2"
          >
            {hasSavedDecks ? (
              <FolderOpen className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">{t('controls.savedDecks')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('controls.savedDecks')}</TooltipContent>
      </Tooltip>

      {/* Copy Button (optional) */}
      {showCopyButton && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCopyCode}
              className="gap-2"
            >
              <Copy className="h-4 w-4" />
              <span className="hidden sm:inline">{t('controls.copyCode', 'Copy')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('controls.copyCode', 'Copy Code')}</TooltipContent>
        </Tooltip>
      )}

      {/* Share Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onShare}
            className="gap-2"
          >
            <Share2 className="h-4 w-4" />
            <span className="hidden sm:inline">{t('controls.shareDeck', 'Share')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('controls.shareDeck', 'Share Deck')}</TooltipContent>
      </Tooltip>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Export Image Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onExportImage}
            disabled={exportImageLoading}
            className="gap-2"
          >
            {exportImageLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Image className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">
              {exportImageLoading 
                ? t('controls.generating', 'Generating...') 
                : t('controls.generateDeckImage', 'Image')}
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('controls.generateDeckImage', 'Generate Deck Image')}</TooltipContent>
      </Tooltip>

      {/* TTS Export Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onExportTTS}
            disabled={isMobile}
            className="gap-2"
          >
            <Table2 className="h-4 w-4" />
            <span className="hidden sm:inline">{t('controls.exportToTTS', 'TTS')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('controls.exportToTTS', 'Export to TTS')}</TooltipContent>
      </Tooltip>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Help Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onHelp}
            className="gap-2"
          >
            <HelpCircle className="h-4 w-4" />
            <span className="hidden sm:inline">{t('controls.help', 'Help')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('controls.help', 'Help')}</TooltipContent>
      </Tooltip>
    </div>
  );
}
